import { ReactElement, useMemo, useCallback } from 'react';
import { useSnackbar } from 'notistack';

import { useSubscriptionFeatures, useLazyRequest } from '~hooks/index';
import { updateItemPreset } from '~services/item';
import { ItemPreset, ItemPresetParse } from '~services/item/types';
import { DialogConfimation } from '~components/index';
import { convertItemSearchFiltersToItemOrCollectFilter } from '~utils/order';
import { camelCaseSerializer } from '~utils/serializer';
import { PlanFeaturesTypes } from '~globals/types/enums';

import { OrdersFiltersPresetsV2DialogConfirmEditProps } from './types';

const OrdersFiltersPresetsV2DialogConfirmEdit = ({
  open,
  onClose,
  selectedPreset,
  filterTabId,
  currentFilters,
  onSuccess,
}: OrdersFiltersPresetsV2DialogConfirmEditProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const messageConfirmation = useMemo(
    () => `El filtro ${selectedPreset.description} será actualizado`,
    [selectedPreset.description],
  );

  const [, loadingEditPreset, , executeEditPreset] = useLazyRequest({
    request: updateItemPreset,
    withPostSuccess: (response) => {
      const presetResponse = response.data?.data as ItemPreset;
      const parsePresetResponse: ItemPresetParse = {
        ...presetResponse,
        data: camelCaseSerializer.serialize(JSON.parse(presetResponse.data)),
      };
      const message = `El filtro ${parsePresetResponse.description} fue actualizado correctamente`;

      onSuccess(parsePresetResponse);

      enqueueSnackbar(message, { variant: 'success' });
    },
    withPostFailure: () => {
      enqueueSnackbar('No se pudo actualizado el filtro, intente nuevamente', {
        variant: 'error',
      });
    },
  });

  const handleEditPreset = useCallback(() => {
    const data = JSON.stringify(
      convertItemSearchFiltersToItemOrCollectFilter(
        filterTabId,
        currentFilters,
        {
          limitNoPro: hasAccessToFeature(PlanFeaturesTypes.LimitNotPro),
          mgmZones: hasAccessToFeature(PlanFeaturesTypes.MgmZones),
        },
      ),
    );

    executeEditPreset({
      presetId: selectedPreset.presetId,
      description: selectedPreset.description,
      data,
    });
  }, [
    filterTabId,
    currentFilters,
    executeEditPreset,
    hasAccessToFeature,
    selectedPreset.description,
    selectedPreset.presetId,
  ]);

  return (
    <DialogConfimation
      open={open}
      description={messageConfirmation}
      onConfirm={handleEditPreset}
      onCancel={onClose}
      loadingConfirm={loadingEditPreset}
    />
  );
};

export default OrdersFiltersPresetsV2DialogConfirmEdit;
